import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';

import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import MuiDialogTitle from '@mui/material/DialogTitle';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Typography from '@mui/material/Typography';

import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';

import Draggable from 'react-draggable';

import PopupMenu from './popupmenu';
import Tooltip from './styled/tooltip';
import StyledPaper from './styled/paper';
// import { GetContrastText } from '../d3/utils';
import DataContext from '../data/data-context.js';
import Auth from '../data/auth-service.js';
import { lavender } from '../data/colors.js';
import useWindowSize from '../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal + 3,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    mitre: {
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        zIndex: theme.zIndex.drawer,
    },
    divider: {
        height: 1,
    },
    paper: {
        maxWidth: 300,
        width: 300,
        '& .MuiListItemIcon-root': {
            minWidth: 0,
        }
    },
    tagsList: {
        maxHeight: 350,
        paddingRight: 0,
        paddingTop: 0,
        flexWrap: 'wrap',
        overflowY: 'scroll',
    },
    listItem: {
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word'
    },
    listEditText: {
        maxWidth: 150,
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word'
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    saveNewButton: {
        position: 'absolute',
        right: theme.spacing(6),
        top: theme.spacing(1),
    },
    titlebar: {
        padding: 0,
    },
    newButton: {
        margin: theme.spacing(1),
    },
    saveButton: {
        margin: 0
    },
    iconButton: {
        margin: 4,
    }
}));

const MitreColors = {
    TA0043: lavender[50],
    TA0042: lavender[50],
    TA0001: lavender[75],
    TA0002: lavender[75],
    TA0003: lavender[100],
    TA0004: lavender[100],
    TA0005: lavender[150],
    TA0006: lavender[150],
    TA0007: lavender[200],
    TA0008: lavender[200],
    TA0009: lavender[250],
    TA0011: lavender[300],
    TA0010: lavender[350],
    TA0040: lavender[400],
};

const Mitre = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const { mitreCategories, mitreCategoriesMap } = useContext(DataContext);
    const [readonly] = useState(!Auth.permissions.get('Alerts').write);

    const [name, setName] = useState();

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleClose = useCallback((event) => {
        setAnchorEl(null);
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleOnUnmitre = useCallback((e) => {
        props.onUnmitre && props.onUnmitre(props.mitre);
        handleClose(e);
    }, [handleClose, props]);

    const handleOnUpdateMitre = useCallback((e, mitre) => {
        props.onUpdateMitre && props.onUpdateMitre(mitre);
        handleClose(e);
    }, [handleClose, props]);

    // incoming
    useEffect(() => {
        const t = mitreCategoriesMap[props.mitre] || { name: props.mitre };
        setName(t.name);
    }, [props.mitre, mitreCategoriesMap]);

    return !props.mitre ? null : (
        <div className={classes.mitre}>
            <Tooltip title={mitreCategoriesMap[props.mitre]
                ? mitreCategoriesMap[props.mitre].description : props.mitre} arrow placement="top">
                <Chip
                    label={name}
                    className={classes.chip}
                    size="small"
                    style={{
                        backgroundColor: MitreColors[props.mitre],
                        color: theme.palette.getContrastText(MitreColors[props.mitre])
                    }}
                    onClick={readonly || props.noclick ? undefined : handleClick}/>
            </Tooltip>
            <Popover
                className={classes.popover}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                <StyledPaper className={classes.paper}>
                    {props.onUnmitre &&
                        <Button
                            onClick={e => handleOnUnmitre(e)}
                            className={classes.iconButton}
                            aria-label={'clear ATT&CK category'}>
                            <RemoveIcon fontSize='small'/>
                            {'Clear'}
                        </Button>
                    }
                    {props.onUpdateMitre &&
                        <React.Fragment>
                            <Divider className={classes.divider} orientation="horizontal" />
                            {mitreCategories.map(c =>
                                <React.Fragment key={c.name}>
                                    <ListItem
                                        size="small"
                                        button
                                        className={classes.listItem}>
                                        <ListItemText
                                            onClick={e => handleOnUpdateMitre(e, c.id)}
                                            primary={c.name}
                                            secondary={
                                                <React.Fragment>
                                                    <Typography variant="button"
                                                        style={{ paddingRight: 5 }}>
                                                        {`${c.id}:`}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {`${c.description}`}
                                                    </Typography>
                                                </React.Fragment>
                                            }/>
                                    </ListItem>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    }
                </StyledPaper>
            </Popover>
        </div>
    );
};
Mitre.propTypes = {
    mitre: PropTypes.string.isRequired,
    onUnmitre: PropTypes.func,
    onUpdateMitre: PropTypes.func,
    placement: PropTypes.string,
    noclick: PropTypes.bool,
};

function PaperComponent (props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <StyledPaper {...props} />
        </Draggable>
    );
}

const MitreList = (props) => {
    const classes = useStyles();
    const [state, setState] = useState('list');
    const [current, setCurrent] = useState();

    const actions = [
        {
            title: 'Apply to selection',
            action: props.onStartMitre ? mitre => {
                setCurrent(mitre);
                setState('mitre');
                props.onStartMitre();
            } : undefined
        },
        {
            title: 'Apply to all in current filter',
            action: props.onMitreFilter ? mitre => props.onMitreFilter(mitre) : undefined
        },
    ];

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            role={undefined} disablePortal>
            <PaperComponent className={classes.paper}
                style={{ maxWidth: 340, width: 340 }}>
                <div
                    onKeyDown={handleListKeyDown}
                    style={{ cursor: 'move' }}
                    id="draggable-dialog-title">
                    {{
                        list: <Fade in={state === 'list'}>
                            <MitreListList
                                onClose={props.onClickAway}
                                actions={actions.filter(a => a.action)}
                                onUnmitreFilter={props.onUnmitreFilter}/>
                        </Fade>,
                        mitre:
                            current && <MitreListMitering
                                mitre={current}
                                onApply={() => { props.onFinishMitre(current.id); setState('list') }}
                                onClose={() => { props.onFinishMitre(); setState('list') }}/>,
                    }[state]}
                </div>
            </PaperComponent>
        </Popper>
    );
};
MitreList.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    onStartMitre: PropTypes.func,
    onFinishMitre: PropTypes.func,
    onUnmitreFilter: PropTypes.func,
    onMitreFilter: PropTypes.func,
    height: PropTypes.number,
};

const MitreSimpleList = (props) => {
    const classes = useStyles();

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    const handleOnClick = mitre => {
        props.onSelect(mitre);
        props.onClickAway();
    };

    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            role={undefined} disablePortal>
            <StyledPaper className={classes.paper}
                modifiers={[
                    { name: 'flip', options: { enabled: false } },
                    { name: 'preventOverflow', options: { enabled: true } },
                ]}>
                <ClickAwayListener onClickAway={props.onClickAway}>
                    <div onKeyDown={handleListKeyDown}>
                        <MitreListList
                            onClick={handleOnClick}
                            onClose={props.onClickAway}/>
                    </div>
                </ClickAwayListener>
            </StyledPaper>
        </Popper>
    );
};
MitreSimpleList.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};

const MitreListList = React.forwardRef((props, ref) => {
    const classes = useStyles();
    const { mitreCategories } = useContext(DataContext);
    const [search, setSearch] = useState('');
    const { height } = useWindowSize();

    const handleSearchChange = event => {
        setSearch(event.target.value || '');
    };

    // tiny popup to fit a tiny monitor FML (1438 x 674)
    const getPapperStyle = useCallback(() => {
        return { height: Math.min(height / 3, 450) };
    }, [height]);

    return (
        <div ref={ref}>
            <MuiDialogTitle className={classes.titlebar}>
                {(mitreCategories.length >= 11) && <React.Fragment>
                    <IconButton
                        className={classes.iconButton}
                        disabled={!search}
                        aria-label="search"
                        size="large">
                        <SearchIcon />
                    </IconButton>
                    <InputBase
                        className={classes.input}
                        onChange={handleSearchChange}
                        value={search}
                        placeholder={'Search ATT&CK categories'}
                        inputProps={{ 'aria-label': 'Search', spellCheck: false }}/>
                </React.Fragment>}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={props.onClose}
                    size="large">
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </MuiDialogTitle>
            {props.onUnmitreFilter && <Button
                color="primary"
                size="small"
                className={classes.newButton}
                onClick={props.onUnmitreFilter}>
                Clear all in current filter
            </Button>}
            <Box>
                <List className={classes.tagsList} style={getPapperStyle()}>
                    {mitreCategories
                        .filter(mitre => search.length < 0 ||
                            `${mitre.name}${mitre.id}${mitre.description}`.includes(search))
                        .map((mitre, i) =>
                            <React.Fragment key={mitre.id}>
                                <ListItem
                                    size="small"
                                    button
                                    className={classes.listItem}>

                                    {props.actions && props.actions.length > 0 &&
                                        <PopupMenu
                                            className={classes.root}
                                            style={{ marginTop: -45 }}
                                            items={props.actions}
                                            itemkey={'title'}
                                            onSelect={sel => sel.action(mitre)}
                                            icon={<MenuIcon/>}
                                            size='small'
                                            placement={'bottom-end'}
                                            button={<ListItemText
                                                primary={mitre.name}
                                                secondary={
                                                    <React.Fragment>
                                                        <Typography variant="button"
                                                            style={{ paddingRight: 5 }}>
                                                            {`${mitre.id}:`}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {`${mitre.description}`}
                                                        </Typography>
                                                    </React.Fragment>
                                                }/>}/>}
                                </ListItem>
                            </React.Fragment>
                        )}
                </List>
            </Box>
        </div>
    );
});
MitreListList.displayName = 'MitreListList';
MitreListList.propTypes = {
    onClose: PropTypes.func.isRequired,
    actions: PropTypes.array,
    onUnmitreFilter: PropTypes.func,
};

const MitreListMitering = (props) => {
    const classes = useStyles();

    return props.mitre ? <Box>
        <ListItem
            size="small"
            button>
            <ListItemText className={classes.listEditText} primary={props.mitre.name} />
            <React.Fragment>
                <ListItemSecondaryAction>
                    <IconButton
                        onClick={props.onApply}
                        edge="end"
                        color="primary"
                        className={classes.saveButton}
                        aria-label="save"
                        size="large">
                        <DoneIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        onClick={props.onClose}
                        edge="end"
                        className={classes.saveButton}
                        aria-label="cancel"
                        size="large">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </ListItemSecondaryAction>
            </React.Fragment>
        </ListItem>
    </Box> : '';
};
MitreListMitering.propTypes = {
    mitre: PropTypes.object,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export {
    Mitre as default,
    MitreList, // for data pages
    MitreSimpleList, // for detail pages
    MitreColors
};
